'use client';
/**
 * ./src/modules/brand/components/BrandShopSamples.tsx
 *
 * BrandShopSamples component to display the samples carousel in the Brand page.
 *
 * @copyright DesignShop, 2024
 * @since 12/16/2024
 */
import { createRef, useState } from 'react';

import { BrandPageCollectionQuery } from '@/modules/brand/gql/__generated/brandPageCollection.generated';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/modules/common/components/carousel';
import Link from '@/modules/common/components/ui/Link';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { PRODUCT_INDEX } from '@/modules/product/components/collections/constants';
import { useCarouselCurrent } from '@/modules/product/hooks/useCarouselCurrent';
import { useSampleCardVariants } from '@/modules/product/hooks/useSampleCardVariants';
import { useSkuSearch } from '@/modules/product/hooks/useSkuSearch';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { SampleCard } from '@/modules/sample-card';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

type BrandShopSamplesProps = {
  samples?: NonNullable<
    NonNullable<
      NonNullable<BrandPageCollectionQuery['brandPageCollection']>['items'][0]
    >['samplesCollection']
  >['items'];
  brand: string;
  slug: string;
};
export const BrandShopSamples = ({ samples = [], brand }: BrandShopSamplesProps) => {
  const enabledSamples = samples.filter((sample) => sample?.enabled);
  const [activeCategory, setActiveCategory] = useState(enabledSamples[0]?.category);
  const [isScrollingSampleCard, setIsScrollingSampleCard] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const current = useCarouselCurrent(api);
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { products, isPending } = useSkuSearch(
    enabledSamples.flatMap((sample) => sample?.skus || []) as string[]
  );
  const { enhancedProducts } = useSampleCardVariants(products);

  const categoriesWithProducts = enabledSamples.map((sample) => ({
    name: sample?.category,
    products: enhancedProducts.filter((product) =>
      sample?.skus?.find((sku) => product?.sku?.includes(sku || ''))
    ),
  }));
  const activeCategoryData = categoriesWithProducts.find(
    (category) => category.name === activeCategory
  );

  // Check if any category has products available
  const hasAnyProducts = categoriesWithProducts.some((cat) => cat.products.length > 0);

  const exploreAllHref = `/search?brands=${encodeURIComponent(brand?.replace(' Samples', ''))}`;

  const handleCarouselInteraction = (isDragging: boolean) => {
    if (isDragging !== isScrollingSampleCard) {
      setIsScrollingSampleCard(isDragging);
    }
  };

  const productRefs =
    activeCategoryData?.products.map(() => {
      return createRef<HTMLDivElement>();
    }) || [];
  const itemListType = (activeCategory || '').toLowerCase().replace(' ', '-');
  const itemListId = `${PRODUCT_INDEX}-${itemListType}`;
  const itemListName = `Brand Shop Samples - ${activeCategory}`;
  useVisibleItemsTracking({
    feature: DataLayerFeature.Brand,
    itemListId,
    itemListName,
    itemListType,
    productRefs,
    products: activeCategoryData?.products,
    shoppingListProducts,
    where: DataLayerWhere.ShopSamples,
  });

  // Don't render if no samples configured, still loading, or no products available
  if (enabledSamples.length === 0 || isPending || !hasAnyProducts) {
    return null;
  }

  return (
    <div className="mx-auto w-full">
      <h2 className="mb-4 px-4 text-2xl font-bold lg:px-8 lg:text-[28px]/[32px]">Shop Samples</h2>

      <Carousel
        className="w-full"
        setApi={setApi}
        opts={{
          watchDrag: !isScrollingSampleCard,
        }}
      >
        <div className="mb-5 flex items-center justify-between px-4 lg:px-8">
          <div className="flex gap-6">
            {enabledSamples.map((sample) => (
              <button
                key={sample?.category}
                onClick={() => setActiveCategory(sample?.category)}
                className={`-mb-px text-nowrap px-1 pb-2 text-base transition-all lg:text-2xl ${
                  activeCategory === sample?.category ?
                    'font-bold shadow-[0_4px_0_0_black]'
                  : 'text-gray-450'
                }`}
              >
                {sample?.category}
              </button>
            ))}
          </div>
          <div className="hidden gap-3 lg:flex">
            <CarouselPrevious
              className="static size-9 translate-x-0 translate-y-0 bg-gray-100 hover:bg-gray-75 disabled:bg-gray-75"
              classNameIcon="text-gray-450"
            />
            <CarouselNext
              classNameIcon="text-gray-450"
              className="static size-9 translate-x-0 translate-y-0 bg-gray-100 hover:bg-gray-75 disabled:bg-gray-75"
            />
          </div>
        </div>
        <CarouselContent className="gap-[10px] px-4 lg:px-0">
          {activeCategoryData?.products.map((product, index) => (
            <CarouselItem key={index} className="w-full max-w-[300px]">
              <div
                ref={productRefs[index]}
                data-product-id={product.id}
                data-index={index}
                className="flex h-full flex-col"
              >
                <SampleCard
                  index={index}
                  key={product.sku}
                  {...product}
                  where={DataLayerWhere.Brand}
                  feature={DataLayerFeature.PLP}
                  item_list_id={itemListId}
                  item_list_name={itemListName}
                  item_list_type={itemListType}
                  onCarouselInteraction={handleCarouselInteraction}
                  enableGallerySliderSwipe={current === index}
                  variant={'carousel'}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <Link href={exploreAllHref} className="flex w-full items-center px-4 lg:px-0" ref={undefined}>
        <Button className="mx-auto mb-10 mt-6 w-full lg:mb-[92px] lg:mt-8 lg:w-fit">
          Explore all {brand}
        </Button>
      </Link>
    </div>
  );
};
